import React from "react";
import { Link } from "@tanstack/react-router";
import { useI18n } from "@/context/I18nContext";

interface MarkdownRendererProps {
  content: string;
}

export function MarkdownRenderer({ content }: MarkdownRendererProps) {
  const { t } = useI18n();
  // Split by paragraph blocks
  const blocks = content.split(/\n\n+/);

  const renderInline = (text: string) => {
    // Parse links [text](url), bold **text**, italics *text* / _text_, inline code `code`, and math $math$
    const parts = text.split(
      /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*\n]+\*|_[^_\n]+_|`[^`\n]+`|\$[^$\n]+\$)/g,
    );
    return parts.map((part, idx) => {
      if (!part) return null;

      // 1. Link [label](url)
      const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        const [, label, href] = linkMatch;
        // Blog content is authored internally today, but this renders
        // whatever markdown it's given — reject javascript:/data: etc. so a
        // future CMS-sourced or user-influenced post can't smuggle a script
        // URL through a link.
        const isSafeScheme = href.startsWith("/") || /^https?:\/\//i.test(href);
        if (!isSafeScheme) {
          return label;
        }
        if (href.startsWith("/")) {
          return (
            <Link
              key={idx}
              to={href}
              className="font-semibold text-mint-ink underline decoration-mint-ink/40 underline-offset-2 hover:decoration-mint-ink transition-colors"
            >
              {label}
            </Link>
          );
        }
        return (
          <a
            key={idx}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-mint-ink underline decoration-mint-ink/40 underline-offset-2 hover:decoration-mint-ink transition-colors"
          >
            {label}
          </a>
        );
      }

      // 2. Bold **text**
      if (part.startsWith("**") && part.endsWith("**") && part.length >= 4) {
        return (
          <strong key={idx} className="font-bold text-navy">
            {part.slice(2, -2)}
          </strong>
        );
      }

      // 3. Italics *text* or _text_
      if (
        ((part.startsWith("*") && part.endsWith("*")) ||
          (part.startsWith("_") && part.endsWith("_"))) &&
        part.length >= 2
      ) {
        return (
          <em key={idx} className="italic text-navy/90">
            {part.slice(1, -1)}
          </em>
        );
      }

      // 4. Inline code `code`
      if (part.startsWith("`") && part.endsWith("`") && part.length >= 2) {
        return (
          <code
            key={idx}
            className="rounded-md bg-navy/5 px-1.5 py-0.5 font-mono text-[0.88em] text-navy font-medium border border-navy/10"
          >
            {part.slice(1, -1)}
          </code>
        );
      }

      // 5. Inline math $formula$
      if (part.startsWith("$") && part.endsWith("$") && part.length >= 2) {
        return (
          <span
            key={idx}
            className="font-serif italic text-navy/95 px-1 rounded bg-surface-alt/70 border border-border/40 font-medium inline-block text-[0.92em]"
          >
            {part.slice(1, -1)}
          </span>
        );
      }

      return part;
    });
  };

  return (
    <div className="space-y-6 text-navy/90">
      {blocks.map((block, blockIdx) => {
        const trimmed = block.trim();
        if (!trimmed) return null;

        // 0. Parse Images
        if (trimmed.startsWith("![") && trimmed.includes("](") && trimmed.endsWith(")")) {
          const match = trimmed.match(/^!\[([^\]]*)\]\(([^)]*)\)$/);
          if (match) {
            const alt = match[1];
            const src = match[2];
            return (
              <div
                key={blockIdx}
                className="my-6 overflow-hidden rounded-2xl border border-border/80 bg-background shadow-md max-w-2xl mx-auto"
              >
                <img src={src} alt={alt} className="w-full object-cover max-h-[400px]" />
                {alt && (
                  <div className="bg-surface-alt/40 border-t p-2.5 text-center text-xs text-muted-foreground font-semibold">
                    {alt}
                  </div>
                )}
              </div>
            );
          }
        }

        // 1. Parse Headings
        if (trimmed.startsWith("###")) {
          return (
            <h3 key={blockIdx} className="font-display text-xl font-bold text-navy mt-6 mb-2">
              {renderInline(trimmed.replace(/^###\s+/, ""))}
            </h3>
          );
        }
        if (trimmed.startsWith("##")) {
          return (
            <h2
              key={blockIdx}
              className="font-display text-2xl font-bold text-navy mt-8 mb-3 border-b pb-2"
            >
              {renderInline(trimmed.replace(/^##\s+/, ""))}
            </h2>
          );
        }
        if (trimmed.startsWith("#")) {
          return (
            <h1 key={blockIdx} className="font-display text-3xl font-bold text-navy mt-10 mb-4">
              {renderInline(trimmed.replace(/^#\s+/, ""))}
            </h1>
          );
        }

        // 2. Parse Display Math Blocks ($$...$$)
        if (trimmed.startsWith("$$") && trimmed.endsWith("$$")) {
          const mathExpr = trimmed.slice(2, -2).trim();
          return (
            <div
              key={blockIdx}
              className="my-6 flex flex-col items-center justify-center rounded-2xl border border-navy/15 bg-gradient-to-br from-surface-alt to-surface-alt/40 p-6 shadow-sm overflow-x-auto text-center"
            >
              <div className="font-serif text-lg md:text-xl font-semibold italic text-navy tracking-wide">
                {mathExpr}
              </div>
            </div>
          );
        }

        // 3. Parse Blockquotes (> ...)
        if (trimmed.startsWith(">")) {
          const quoteText = trimmed
            .split("\n")
            .map((line) => line.replace(/^>\s?/, ""))
            .join("\n");
          return (
            <blockquote
              key={blockIdx}
              className="my-6 border-s-4 border-mint-ink bg-surface-alt/50 ps-5 pe-4 py-3 rounded-e-2xl text-navy/85 italic text-base leading-relaxed"
            >
              {renderInline(quoteText)}
            </blockquote>
          );
        }

        // 4. Parse Numbered (Ordered) Lists (1. item\n2. item)
        if (/^\d+\.\s+/.test(trimmed)) {
          const items = trimmed.split(/\n\d+\.\s+/);
          items[0] = items[0].replace(/^\d+\.\s+/, "");
          return (
            <ol key={blockIdx} className="list-decimal ps-6 space-y-2.5 my-4 text-navy/85">
              {items.map((item, itemIdx) => (
                <li key={itemIdx} className="leading-relaxed">
                  {renderInline(item)}
                </li>
              ))}
            </ol>
          );
        }

        // 5. Parse Bullet (Unordered) Lists (- item or * item)
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          const items = trimmed.split(/\n[*-]\s+/);
          // clean first item prefix
          items[0] = items[0].replace(/^[*-]\s+/, "");
          return (
            <ul key={blockIdx} className="list-disc ps-6 space-y-2.5 my-4 text-navy/85">
              {items.map((item, itemIdx) => (
                <li key={itemIdx} className="leading-relaxed">
                  {renderInline(item)}
                </li>
              ))}
            </ul>
          );
        }

        // 6. Parse Tables
        if (trimmed.startsWith("|") && trimmed.includes("\n|")) {
          const lines = trimmed.split("\n").filter((l) => l.trim().startsWith("|"));
          if (lines.length >= 2) {
            // Find headers
            const headerCells = lines[0]
              .split("|")
              .map((c) => c.trim())
              .filter((c, i, a) => i > 0 && i < a.length - 1);

            // Find data rows (skip separator row like | :--- | :--- |)
            const rows = lines
              .slice(1)
              .filter((l) => !l.includes("---"))
              .map((l) =>
                l
                  .split("|")
                  .map((c) => c.trim())
                  .filter((c, i, a) => i > 0 && i < a.length - 1),
              );

            return (
              <div
                key={blockIdx}
                role="region"
                aria-label={headerCells.join(" / ")}
                tabIndex={0}
                className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-sm overflow-x-auto focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint-ink"
              >
                {/* text-start, not text-left: physical alignment would keep Arabic table
                    columns left-aligned inside an rtl document. */}
                <table className="w-full min-w-[520px] text-start border-collapse">
                  <thead>
                    <tr className="bg-navy/5 border-b border-border/80">
                      {headerCells.map((h, idx) => (
                        <th
                          key={idx}
                          className="p-3.5 text-xs font-bold text-navy uppercase tracking-wider text-start"
                        >
                          {renderInline(h)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60 text-sm">
                    {rows.map((row, rowIdx) => (
                      <tr
                        key={rowIdx}
                        className={
                          rowIdx % 2 === 0
                            ? "bg-transparent hover:bg-surface-alt/50 transition-colors"
                            : "bg-surface-alt/25 hover:bg-surface-alt/60 transition-colors"
                        }
                      >
                        {row.map((cell, cellIdx) => (
                          <td
                            key={cellIdx}
                            className="p-3.5 text-navy/85 font-normal align-top leading-relaxed"
                          >
                            {renderInline(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
        }

        // 4. Custom Chart blocks (Render charts beautifully)
        // If we detect specific keywords for visual charts
        if (
          trimmed.includes("Rotifer Yield") &&
          trimmed.includes("350%") &&
          trimmed.includes("800%")
        ) {
          return (
            <div
              key={blockIdx}
              className="my-8 rounded-3xl border border-white/20 bg-gradient-to-br from-navy to-navy-light p-6 text-white shadow-xl relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-mint/10 rounded-full blur-2xl pointer-events-none" />
              <h4 className="font-display text-lg font-bold text-white mb-2">
                {t("markdown.chart.rotifer.title")}
              </h4>
              <p className="text-xs text-white/70 mb-6">{t("markdown.chart.rotifer.subtitle")}</p>

              <div className="space-y-6">
                <div>
                  <div className="flex justify-between text-xs font-semibold mb-2">
                    <span className="text-white/80">{t("markdown.chart.rotifer.bar1")}</span>
                    <span className="text-red-400 font-bold">
                      {t("markdown.chart.rotifer.val1")}
                    </span>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div
                      className="bg-red-400 h-full rounded-full transition-all duration-1000"
                      style={{ width: "35%" }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-semibold mb-2">
                    <span className="text-white/80">{t("markdown.chart.rotifer.bar2")}</span>
                    <span className="text-mint font-bold">{t("markdown.chart.rotifer.val2")}</span>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-mint to-teal-400 h-full rounded-full transition-all duration-1000 shadow-[0_0_12px_rgba(45,212,191,0.3)]"
                      style={{ width: "80%" }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-white/10 flex justify-between text-[10px] text-white/50">
                <span>{t("markdown.chart.rotifer.note")}</span>
                <span>{t("markdown.chart.rotifer.source")}</span>
              </div>
            </div>
          );
        }

        if (
          trimmed.includes("Price vs. Performance") ||
          (trimmed.includes("Artemia") &&
            trimmed.includes("Viable larva") &&
            trimmed.includes("US Artemia"))
        ) {
          return (
            <div
              key={blockIdx}
              className="my-8 rounded-3xl border border-white/20 bg-gradient-to-br from-navy to-navy-light p-6 text-white shadow-xl relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-mint/10 rounded-full blur-2xl pointer-events-none" />
              <h4 className="font-display text-lg font-bold text-white mb-2">
                {t("markdown.chart.artemia.title")}
              </h4>
              <p className="text-xs text-white/70 mb-6">{t("markdown.chart.artemia.subtitle")}</p>

              <div className="space-y-6">
                <div>
                  <div className="flex justify-between text-xs font-semibold mb-2">
                    <span className="text-white/80">{t("markdown.chart.artemia.bar1")}</span>
                    <span className="text-white font-bold">{t("markdown.chart.artemia.val1")}</span>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className="bg-white/40 h-full rounded-full" style={{ width: "100%" }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-semibold mb-2">
                    <span className="text-white/80">{t("markdown.chart.artemia.bar2")}</span>
                    <span className="text-mint font-bold">{t("markdown.chart.artemia.val2")}</span>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-mint to-teal-400 h-full rounded-full transition-all duration-1000 shadow-[0_0_12px_rgba(45,212,191,0.3)]"
                      style={{ width: "60%" }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-white/10 flex justify-between text-[10px] text-white/50">
                <span>{t("markdown.chart.artemia.note")}</span>
                <span>{t("markdown.chart.artemia.source")}</span>
              </div>
            </div>
          );
        }

        // 8. Parse Code / Monospace blocks (```...```)
        if (trimmed.startsWith("```") && trimmed.endsWith("```")) {
          const innerCode = trimmed.replace(/^```[a-zA-Z0-9_-]*\n?/, "").replace(/\n?```$/, "");
          return (
            <div
              key={blockIdx}
              className="my-6 overflow-x-auto rounded-2xl border border-navy/10 bg-navy/5 p-4 text-navy font-mono text-xs shadow-inner"
            >
              <pre className="whitespace-pre font-mono">{innerCode}</pre>
            </div>
          );
        }

        // 9. Default: Paragraph
        return (
          <p key={blockIdx} className="text-navy/85 leading-relaxed text-base font-normal my-4">
            {renderInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
}
